import React, { useCallback, useEffect, useRef } from "react";

import { type TableRef } from "@performyard/py-components";

import { type DecisionRow } from "../../../types";

import { useDecisionNotesAutosave } from "../../../../../hooks/useDecisionNotesAutosave";

import { updateMyDecisionsNotes } from "../../../../../api/services/defs/internal-api/compensation";

import { notesPayloadKey } from "../../CellRenderers";

export const useDecisionNotes = (args: {
	baseDecisionRowsFlat: DecisionRow[];
	employeeIdToManagerId: Map<string, string>;
	gridRefs: React.MutableRefObject<Record<string, TableRef<DecisionRow>>>;
	cycleId: string | undefined;
	userID: string | undefined;
}) => {
	const { baseDecisionRowsFlat, employeeIdToManagerId, gridRefs, cycleId, userID } = args;

	const notesValuesRef = useRef<Record<string, string>>({});
	const activeNoteEditorRef = useRef<string | null>(null);

	useEffect(() => {
		const next = { ...notesValuesRef.current };
		let changed = false;
		for (const r of baseDecisionRowsFlat) {
			const key = notesPayloadKey(r);
			if (!key) continue;
			const text = r.notes;
			if (text != null && text !== "" && next[key] === undefined) {
				next[key] = text;
				changed = true;
			}
		}
		if (changed) {
			notesValuesRef.current = next;
		}
	}, [baseDecisionRowsFlat]);

	const { schedulePersist: scheduleNotesAutosave } = useDecisionNotesAutosave({
		notesValuesRef,
		cycleId,
		managerId: userID,
		persist: updateMyDecisionsNotes,
		debounceMs: 800,
	});

	const onNotesChange = useCallback(
		(employeeId: string, value: string) => {
			notesValuesRef.current = { ...notesValuesRef.current, [employeeId]: value };
			scheduleNotesAutosave();

			const managerId = employeeIdToManagerId.get(employeeId);
			const gridsToPatch =
				managerId != null && gridRefs.current[managerId]
					? [gridRefs.current[managerId]]
					: Object.values(gridRefs.current);

			for (const grid of gridsToPatch) {
				const api = grid?.api;
				if (!api) continue;
				api.forEachNode((node) => {
					const d = node.data;
					if (d && !d.superRow && String(d.employee_id) === employeeId) {
						node.setData({ ...d, notes: value });
					}
				});
			}
		},
		[employeeIdToManagerId, scheduleNotesAutosave]
	);

	return { notesValuesRef, activeNoteEditorRef, onNotesChange };
};
